import {APIRegistry} from '@spothero/utils/api';
import Config from 'config';

const createAPIRegistry = () => {
    if (!APIRegistry.get('craig')) {
        APIRegistry.create({
            name: 'craig',
            config: {
                baseUrl: Config.craigUrl,
            },
        });
    }
};

const isValidRateRange = (low, high) =>
    Boolean(parseFloat(low)) && Boolean(parseFloat(high));

const DynamicRatesAPI = {
    /**
     * Gets a list of aggregated rates for given city.
     *
     * @function getRatesByCity
     * @see {@link https://api.spothero.com/v2/docs#operation/fetchCityRates|Documentation}
     * @param {object} citySlug - The monolith-defined city slug to pass to the endpoint.
     * @example
     * getRatesByCity('chicago');
     * @returns {object} Currency and aggregated rates for the city, with data transformation applied.
     */
    getRatesByCity: citySlug => {
        createAPIRegistry();

        return APIRegistry.get('craig')
            .get(`/midfunnel-rates/cities/${citySlug}/`)
            .then(({data}) => {
                const {
                    currency,
                    commuter_avg_price_low: commuterAvgPriceLow,
                    commuter_avg_price_high: commuterAvgPriceHigh,
                    weekend_avg_price_low: weekendAvgPriceLow,
                    weekend_avg_price_high: weekendAvgPriceHigh,
                    event_avg_price_low: eventAvgPriceLow,
                    event_avg_price_high: eventAvgPriceHigh,
                    overnight_avg_price_low: overnightAvgPriceLow,
                    overnight_avg_price_high: overnightAvgPriceHigh,
                    monthly_avg_price_low: monthlyAvgPriceLow,
                    monthly_avg_price_high: monthlyAvgPriceHigh,
                    airport_avg_price_low: airportAvgPriceLow,
                    airport_avg_price_high: airportAvgPriceHigh,
                } = data;
                const hasCommuterAvg = isValidRateRange(
                    commuterAvgPriceLow,
                    commuterAvgPriceHigh
                );
                const hasWeekendAvg = isValidRateRange(
                    weekendAvgPriceLow,
                    weekendAvgPriceHigh
                );
                const hasEventAvg = isValidRateRange(
                    eventAvgPriceLow,
                    eventAvgPriceHigh
                );
                const hasOvernightAvg = isValidRateRange(
                    overnightAvgPriceLow,
                    overnightAvgPriceHigh
                );
                const hasMonthlyAvg = isValidRateRange(
                    monthlyAvgPriceLow,
                    monthlyAvgPriceHigh
                );
                const hasAirportAvg = isValidRateRange(
                    airportAvgPriceLow,
                    airportAvgPriceHigh
                );

                const transformedData = {
                    currencyCode: currency.toUpperCase(),
                    commuterAvgPrice: hasCommuterAvg
                        ? {
                              low: parseFloat(commuterAvgPriceLow),
                              high: parseFloat(commuterAvgPriceHigh),
                          }
                        : null,
                    weekendAvgPrice: hasWeekendAvg
                        ? {
                              low: parseFloat(weekendAvgPriceLow),
                              high: parseFloat(weekendAvgPriceHigh),
                          }
                        : null,
                    eventAvgPrice: hasEventAvg
                        ? {
                              low: parseFloat(eventAvgPriceLow),
                              high: parseFloat(eventAvgPriceHigh),
                          }
                        : null,
                    overnightAvgPrice: hasOvernightAvg
                        ? {
                              low: parseFloat(overnightAvgPriceLow),
                              high: parseFloat(overnightAvgPriceHigh),
                          }
                        : null,
                    monthlyAvgPrice: hasMonthlyAvg
                        ? {
                              low: parseFloat(monthlyAvgPriceLow),
                              high: parseFloat(monthlyAvgPriceHigh),
                          }
                        : null,
                    airportAvgPrice: hasAirportAvg
                        ? {
                              low: parseFloat(airportAvgPriceLow),
                              high: parseFloat(airportAvgPriceHigh),
                          }
                        : null,
                };

                return transformedData;
            })
            .catch(() => {
                // We probably don't want the midfunnel page (or any other page)
                // to 404 if this API fails
                return {
                    currencyCode: 'USD',
                    commuterAvgPrice: null,
                    weekendAvgPrice: null,
                    eventAvgPrice: null,
                    overnightAvgPrice: null,
                    monthlyAvgPrice: null,
                    airportAvgPrice: null,
                };
            });
    },
};

export default DynamicRatesAPI;
